import type { WSMessage } from '@tunasong/models'
import { logger, networkBus } from '@tunasong/models'
import { getFreshIdToken, OAuth, useSelector, useThunkDispatch } from '@tunasong/redux'
import { useOnlineStatus } from '@tunasong/ui-lib'
import { useEffect, useRef, useState } from 'react'

export interface WebSocketProps {
  baseUrl?: string
  /** Reconnect even on normal close (i.e., return code 1000) */
  persistent?: boolean
  /** Allow non-authenticated users. @default false */
  allowPublic?: boolean
}

const WEBSOCKET_NORMAL_CLOSE = 1000

export const useCreateWebsocket = (props: WebSocketProps = {}) => {
  /** The endpoint is deployment config, not a constant: a local host serves /ws on its own origin */
  const configuredBaseUrl = useSelector(state => state.config.wsBaseUrl)
  const { allowPublic = false, baseUrl = configuredBaseUrl, persistent = true } = props
  const [webSocket, setWebsocket] = useState<WebSocket | null>(null)
  /** Connection number - used to trigger reconnects in hook */
  const [connId, setConnId] = useState(1)
  const online = useOnlineStatus()

  /** Use a ref to avoid re-render (and closing ws) when retryWait changes */
  const retryWait = useRef(2000)

  const dispatch = useThunkDispatch()

  const authData = useSelector(state => state.user.authData)

  /** @todo refactor to use useEffectEvent */
  useEffect(() => {
    if (!online) {
      logger.info('Websocket: Offline, not attempting connect')
      return
    }

    /**
     * The socket is authenticated once, at $connect, so a live socket outlives its 60-minute ID
     * token — and API Gateway ends every connection after 2 h. Expiry is checked here, when we
     * connect, and never a dependency: an expired token must not close a working socket.
     *
     * An expired token is refreshed rather than waited out. Nothing else refreshes it for an idle
     * tab (only a 401 on an HTTP call does), so bailing here left the tab disconnected for good,
     * silently missing every peer edit until a reload. `setAuth` brings a new `authData`, which
     * re-runs this effect and connects.
     */
    if (authData && OAuth.isSessionExpired(authData)) {
      let retryTimeoutId: ReturnType<typeof setTimeout> | null = null
      dispatch((thunkDispatch, getState) => getFreshIdToken({ dispatch: thunkDispatch, getState })).catch(
        (error: unknown) => {
          logger.warn(`Websocket: token refresh failed, retrying in ${retryWait.current} ms`, error)
          retryTimeoutId = setTimeout(() => setConnId(prev => prev + 1), retryWait.current)
          retryWait.current = Math.min(retryWait.current * 2, 60000)
        }
      )
      return () => {
        if (retryTimeoutId !== null) {
          clearTimeout(retryTimeoutId)
        }
      }
    }

    const t = authData?.idToken.jwtToken ?? (allowPublic ? 'public' : undefined)
    if (!t) {
      return
    }

    const url = `${baseUrl}?token=${t}`
    const ws = new WebSocket(url)
    networkBus.emit('ws-status-changed', { status: 'connecting' })

    /** Ping handler to avoid AWS disconnect. Hoisted into the effect scope so
     *  the cleanup function below can `clearInterval` regardless of which
     *  branch (open/close/error/effect-teardown) ran last. Previously this
     *  was declared inside `openHandler` and the `return () => clearInterval`
     *  was dead code (DOM event listeners ignore returned cleanup functions),
     *  so every reconnect leaked another 5-minute ping interval — GH #1168. */
    let pingIntervalId: ReturnType<typeof setInterval> | null = null

    const openHandler = () => {
      if (ws.readyState !== 1) {
        throw new Error(`Websocket failed to open`)
      }
      logger.debug(`*** Websocket connected ***`)
      setWebsocket(ws)

      /** Reset the exponential backoff so a single dropped connection on a
       *  flaky mobile link doesn't permanently raise reconnect latency to
       *  60 s — GH #1168. */
      retryWait.current = 2000
      networkBus.emit('ws-status-changed', { status: 'connected' })

      const pingMsg: WSMessage<unknown> = {
        action: 'ping',
        data: {},
      }
      /** ping interval is 5 minutes */
      const pingInterval = 5 * 60 * 1000
      pingIntervalId = setInterval(() => {
        if (ws.readyState === 1) {
          ws.send(JSON.stringify(pingMsg))
        }
      }, pingInterval)
    }

    const closeHandler = (ev: CloseEvent) => {
      logger.info('*** Websocket closed.', ev)
      setWebsocket(null)
      networkBus.emit('ws-status-changed', { status: 'disconnected' })
      /**
       * See close codes here https://tools.ietf.org/html/rfc6455#section-7.4.1
       * 1000 is "normal close", which means we should not reconnect
       * 1006 is close before connected, which can happen on startup when token is not set
       */
      if (!persistent && (ev.code === 1000 || ev.code === 1006)) {
        return
      }
      /** Since this was NOT triggered by our useEffect hook we re-connect */
      logger.info(`*** Websocket reconnecting in ${retryWait.current} ms...`)

      setTimeout(() => setConnId(prev => prev + 1), retryWait.current)
      /** Update the retry wait time. Increase expontially, cap at 60 seconds. */
      retryWait.current = Math.min(retryWait.current * 2, 60000)
    }

    /**
     * A failed connection fires `close` right after `error`, and the close handler reconnects. A
     * reconnect with an expired token refreshes it above. Never touch the auth state here: flipping
     * `isLoggedIn` makes AuthGuard unmount the whole app behind a full-page spinner.
     */
    const errorHandler = (ev: Event) => {
      logger.error('*** Websocket error', ev)
      setWebsocket(null)
    }

    ws.addEventListener('open', openHandler)
    ws.addEventListener('close', closeHandler)
    ws.addEventListener('error', errorHandler)

    return () => {
      // logger.log('Websocket hook -  removing event listeners for connection', connId, url)
      ws.removeEventListener('open', openHandler)
      ws.removeEventListener('close', closeHandler)
      ws.removeEventListener('error', errorHandler)
      /** Clear any active ping interval — see hoisting note above (GH #1168). */
      if (pingIntervalId !== null) {
        clearInterval(pingIntervalId)
        pingIntervalId = null
      }
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        logger.debug('Websocket closing connection', { readyState: ws.readyState, connId })
        ws.close(WEBSOCKET_NORMAL_CLOSE)
      }
    }
    /** @note we use connId here to trigger a re-connect. connId is only set in the close handler */
  }, [allowPublic, baseUrl, connId, authData, persistent, dispatch, online])

  return webSocket
}
